/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AbstractFilterComponent} from '../abstract-filter.component';
import {InputTextComponent} from '../../input/input-text/input-text.component';
import {InputDropdownComponent} from '../../input/input-dropdown/input-dropdown.component';
import {UntypedFormBuilder, UntypedFormControl} from '@angular/forms';
import {NumberFilterOperatorModel} from './number-filter-operator.model';
import {NumberFilterOperatorEnum} from './number-filter-operator.enum';
import {Observable, of, Subscription} from 'rxjs';
import {NumberFilterStateModel} from './number-filter-state.model';
import {NumberFilterOptionEnum} from './number-filter-option.enum';

@Component({
    selector: 'app-number-filter',
    templateUrl: './number-filter.component.html',
    styleUrls: ['./number-filter.component.css', '../filter.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NumberFilterComponent extends AbstractFilterComponent<NumberFilterStateModel> implements OnInit, OnDestroy {
    /** Component configuration variables **/
    @Input() operatorPlaceholder: string;

    @ViewChild('defaultInput', {static: true}) defaultInput: InputTextComponent;
    @ViewChild('extraInput', {static: true}) extraInput: InputTextComponent;
    @ViewChild('operator', {static: true}) operator: InputDropdownComponent;

    /** Filter form variables **/
    public showExtraInputField = false;
    public showExtraInputFieldClass: string;

    public form = this.formBuilder.group({
        option: new UntypedFormControl()
    });

    /** Dropdown options variables **/
    public dropdownOptions: NumberFilterOperatorModel[] = [
        {label: 'Equal to', value: NumberFilterOperatorEnum.EQUAL},
        {label: 'Bigger than', value: NumberFilterOperatorEnum.BIGGER_THAN},
        {label: 'Equal or bigger than', value: NumberFilterOperatorEnum.EQUAL_BIGGER},
        {label: 'Smaller than', value: NumberFilterOperatorEnum.SMALLER_THAN},
        {label: 'Equal or smaller than', value: NumberFilterOperatorEnum.EQUAL_SMALLER},
        {label: 'Between', value: NumberFilterOperatorEnum.BETWEEN},
    ];

    NumberFilterOptionEnum = NumberFilterOptionEnum;

    private subscriptions = new Subscription();

    constructor(private formBuilder: UntypedFormBuilder) {
        super();
    }

    operatorOptionFormatter = (option: NumberFilterOperatorModel): string => {
        if (option == null) {
            return null;
        }

        return option.label;
    };

    operatorFilterFunction = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.dropdownOptions.filter(option => option.label
                    .toLowerCase().indexOf(term.toLowerCase()) > -1));
        }
        return of(this.dropdownOptions);
    };

    ngOnInit(): void {
        this.form.get('option').setValue(NumberFilterOptionEnum.ALL);
        this.subscriptions.add(this.form.get('option').valueChanges.subscribe(value => {
            this.storeState({option: value});
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onOperatorChange(value: NumberFilterOperatorModel & string): void {
        this.storeState({operator: value});

        this.form.get('option').setValue(NumberFilterOptionEnum.CHOOSE);

        if (value != null && this.dropdownOptions.find(option => option.value === value.value)) {
            this.showExtraInputField = value.value === NumberFilterOperatorEnum.BETWEEN;

            if (this.defaultInput != null) {
                this.defaultInput.enable();
            }

            if (this.extraInput != null) {
                this.extraInput.enable();
            }
        } else {
            this.showExtraInputField = false;

            if (this.defaultInput != null) {
                this.defaultInput.disable();
            }

            if (this.extraInput != null) {
                this.extraInput.disable();
            }
        }

        this.showExtraInputFieldClass = this.showExtraInputField ? 'number-input-two' : 'number-input';
    }

    onOperatorBlur(event: Event): void {
        if (!this.operator.isValid) {
            this.operator.reset();
        }
    }

    updateDefaultValue(value: string): void {
        this.storeState({
            defaultValue: value
        });
    }

    updateExtraValue(value: string): void {
        this.storeState({
            extraValue: value
        });
    }

    emitSearch(): void {
        this.filterEvent.emit(this.state);
    }

    resetFilter(): void {
        if (this.defaultInput != null) {
            this.defaultInput.reset();
        }

        if (this.extraInput != null) {
            this.extraInput.reset();
        }

        this.operator.reset();
        this.form.get('option').setValue(NumberFilterOptionEnum.ALL);
    }

    initFilter(value: NumberFilterStateModel): void {
        if (value != null) {
            this.storeState(value);
            this.form.get('option').setValue(value.option);
            this.operator.setValue(value.operator);
            this.defaultInput.setValue(value.defaultValue);

            if (this.extraInput != null) {
                this.extraInput.setValue(value.extraValue);
            }
        }
    }

    getOperatorPlaceholder(): string {
        return this.showExtraInputField ? '' : this.operatorPlaceholder;
    }

    storeState(state?: Partial<NumberFilterStateModel>): void {
        super.storeState(state);
        super.storeState({
            isActive: this.state.defaultValue != null || this.state.extraValue != null
        });
    }
}

